/**
 * Shared Shopify Ajax Cart helpers for Nimizo.
 * Dispatches `nimizo:cart:updated` with the cart JSON payload.
 */
(() => {
  const root = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let queue = Promise.resolve();
  let drawerBound = false;
  let overlayBound = false;
  let lastDrawerTrigger = null;

  function enqueue(task) {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  }

  async function parseJson(response) {
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok) {
      const message =
        (data && (data.description || data.message || data.errors)) ||
        'Something went wrong updating your bag. Please try again.';
      const error = new Error(typeof message === 'string' ? message : 'Cart update failed');
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    return data;
  }

  async function getCart() {
    const response = await fetch(`${root()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return parseJson(response);
  }

  async function addItem(payload) {
    return enqueue(async () => {
      const body =
        payload && Array.isArray(payload.items)
          ? { items: payload.items }
          : payload;

      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      await parseJson(response);
      return getCart();
    });
  }

  async function changeLine(payload) {
    return enqueue(async () => {
      const quantity = Number(payload?.quantity);
      const body = { quantity: Number.isFinite(quantity) ? quantity : 0 };

      const line = Number(payload?.line);
      if (Number.isFinite(line) && line >= 1) {
        body.line = line;
      } else if (payload?.id != null && String(payload.id).trim() !== '') {
        body.id = String(payload.id).trim();
      } else {
        throw new Error('Missing cart line. Please refresh and try again.');
      }

      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      // Shopify returns the updated cart object from /cart/change.js
      const changed = await parseJson(response);
      if (changed && Array.isArray(changed.items)) return changed;
      return getCart();
    });
  }

  function updateCartCount(cart) {
    const count = cart?.item_count ?? 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count <= 0 && el.hasAttribute('data-cart-count-hide-empty');
      if (el.classList.contains('mobile-cart-badge')) {
        el.hidden = count <= 0;
        if (count > 0) el.textContent = String(count);
      }
    });
  }

  function setPageInert(on) {
    const main = document.getElementById('MainContent');
    const header = document.getElementById('shopify-section-header') || document.querySelector('header');
    [main, header].forEach((el) => {
      if (!el) return;
      if (on) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    });
  }

  function getDrawerFocusable(drawer) {
    return Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]') && el.getClientRects().length > 0);
  }

  async function refreshCartDrawer() {
    const drawer = document.getElementById('NimizoCartDrawer');
    if (!drawer) return false;

    const urls = [
      `${root()}cart?sections=cart-drawer`,
      `${root()}?sections=cart-drawer`,
      `${root()}cart?section_id=cart-drawer`,
    ];

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json, text/html' },
        });
        if (!response.ok) continue;

        const contentType = response.headers.get('content-type') || '';
        let html = '';
        if (contentType.includes('application/json')) {
          const sections = await response.json();
          html = sections['cart-drawer'] || sections[Object.keys(sections)[0]] || '';
        } else {
          html = await response.text();
        }
        if (!html) continue;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next =
          doc.getElementById('NimizoCartDrawer') ||
          doc.querySelector('.nimizo-cart-drawer');
        if (!next) continue;

        const wasOpen = drawer.classList.contains('is-open');
        drawer.replaceWith(next);
        bindCartDrawer();
        if (wasOpen) openCartDrawer({ restoreFocus: false });
        return true;
      } catch (error) {
        console.warn('Cart drawer refresh attempt failed', url, error);
      }
    }

    console.error('Cart drawer HTML could not be refreshed.');
    return false;
  }

  function setQuickAddLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (!btn.dataset.labelHtml) btn.dataset.labelHtml = btn.innerHTML;
      btn.innerHTML = '<span class="product-card__spinner" aria-hidden="true"></span><span>Adding…</span>';
    } else {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (btn.dataset.labelHtml) {
        btn.innerHTML = btn.dataset.labelHtml;
        delete btn.dataset.labelHtml;
      }
    }
  }

  function bindQuickAdd() {
    if (bindQuickAdd.bound) return;
    bindQuickAdd.bound = true;

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest?.('[data-nimizo-quick-add]');
      if (!form) return;
      event.preventDefault();

      const id = Number(form.querySelector('[name="id"]')?.value);
      if (!id) return;

      const btn = form.querySelector('[type="submit"]');
      setQuickAddLoading(btn, true);
      try {
        const cart = await addItem({ id, quantity: 1 });
        await onCartUpdated(cart, { openDrawer: true });
      } catch (error) {
        console.error(error);
        window.alert(error.message || 'Could not add to bag.');
      } finally {
        setQuickAddLoading(btn, false);
      }
    });
  }

  function openCartDrawer({ restoreFocus = true } = {}) {
    const drawer = document.getElementById('NimizoCartDrawer');
    const overlay = document.getElementById('NimizoCartDrawerOverlay');
    if (!drawer) return;
    if (restoreFocus) {
      lastDrawerTrigger = document.activeElement;
    }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-open');
    document.documentElement.classList.add('nimizo-cart-drawer-open');
    setPageInert(true);
    const closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    closeBtn?.focus();
  }

  function closeCartDrawer() {
    const drawer = document.getElementById('NimizoCartDrawer');
    const overlay = document.getElementById('NimizoCartDrawerOverlay');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-open');
    document.documentElement.classList.remove('nimizo-cart-drawer-open');
    setPageInert(false);
    if (lastDrawerTrigger?.focus) {
      lastDrawerTrigger.focus();
      lastDrawerTrigger = null;
    }
  }

  function bindCartDrawer() {
    const drawer = document.getElementById('NimizoCartDrawer');
    const overlay = document.getElementById('NimizoCartDrawerOverlay');
    if (!drawer) return;

    drawer.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', closeCartDrawer);
    });

    if (overlay && !overlayBound) {
      overlayBound = true;
      overlay.addEventListener('click', closeCartDrawer);
    }

    if (drawer.dataset.nimizoQtyBound === '1') return;
    drawer.dataset.nimizoQtyBound = '1';

    drawer.addEventListener('click', async (event) => {
      const qtyBtn = event.target.closest('[data-cart-drawer-qty]');
      const removeBtn = event.target.closest('[data-cart-drawer-remove]');
      if (!qtyBtn && !removeBtn) return;

      const btn = qtyBtn || removeBtn;
      const row = btn.closest('[data-cart-drawer-item]');
      const line = Number(btn.getAttribute('data-line-index') || row?.getAttribute('data-line-index') || 0);
      const key = (btn.getAttribute('data-line-key') || row?.getAttribute('data-line-key') || '').trim();

      try {
        if (qtyBtn) {
          const delta = Number(qtyBtn.getAttribute('data-qty-delta') || 0);
          const current = Number(row?.getAttribute('data-quantity') || 1);
          const next = Math.max(0, current + delta);
          const cart = await changeLine({
            line: line >= 1 ? line : undefined,
            id: key || undefined,
            quantity: next,
          });
          await onCartUpdated(cart, { openDrawer: true });
          return;
        }

        const cart = await changeLine({
          line: line >= 1 ? line : undefined,
          id: key || undefined,
          quantity: 0,
        });
        await onCartUpdated(cart, { openDrawer: true });
      } catch (error) {
        console.error(error);
        window.alert(error.message || 'Could not update cart.');
      }
    });
  }

  async function onCartUpdated(cart, { openDrawer = false } = {}) {
    updateCartCount(cart);
    document.dispatchEvent(new CustomEvent('nimizo:cart:updated', { detail: { cart } }));

    let refreshed = await refreshCartDrawer();
    if (!refreshed) {
      // Retry once after a short delay — section HTML can lag cart.js briefly.
      await new Promise((r) => setTimeout(r, 150));
      refreshed = await refreshCartDrawer();
    }

    if (openDrawer || document.getElementById('NimizoCartDrawer')?.classList.contains('is-open')) {
      openCartDrawer({ restoreFocus: false });
    }
    if (!refreshed) {
      console.warn('Cart updated but drawer HTML could not be refreshed.');
    }
    return cart;
  }

  function initDrawerTriggers() {
    if (drawerBound) return;
    drawerBound = true;
    document.addEventListener('click', (event) => {
      const openBtn = event.target.closest('[data-cart-drawer-open]');
      if (openBtn) {
        event.preventDefault();
        lastDrawerTrigger = openBtn;
        openCartDrawer({ restoreFocus: false });
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (document.querySelector('[data-engraving-dialog][open]')) return;
        closeCartDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const drawer = document.getElementById('NimizoCartDrawer');
      if (!drawer?.classList.contains('is-open')) return;
      if (document.querySelector('[data-engraving-dialog][open]')) return;
      const focusable = getDrawerFocusable(drawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    bindCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initDrawerTriggers();
      bindQuickAdd();
    });
  } else {
    initDrawerTriggers();
    bindQuickAdd();
  }

  window.NimizoCart = {
    getCart,
    addItem,
    changeLine,
    updateCartCount,
    refreshCartDrawer,
    openCartDrawer,
    closeCartDrawer,
    onCartUpdated,
  };
})();
